/**
 * Logout redirect to REACT_APP_LOGOUT_DOMAIN without the app flashing the sign-in / index page.
 *
 * Leaving for another domain takes a moment (the browser waits for that site to respond), and the
 * old page keeps running meanwhile. Logout clears localStorage / Redux, so the app re-rendered as
 * "logged out" and its own redirects (catch-all -> /signin, "not logged in" -> /) showed for a few
 * seconds before the logout page loaded. redirectAfterLogout() covers the page first and tells
 * App.tsx to stop rendering and redirecting until the browser has left.
 */
const FLAG = "__oceshaLoggingOut";
const COVER_ID = "ocesha-logout-cover";

/** REACT_APP_LOGOUT_DOMAIN, tolerant of stray quotes / spaces written into the .env value. */
export const logoutDomain = (): string =>
  (process.env.REACT_APP_LOGOUT_DOMAIN || "").trim().replace(/^['"]+|['"]+$/g, "").trim();

export const isLoggingOut = (): boolean => !!(window as any)[FLAG];

const showCover = () => {
  if (document.getElementById(COVER_ID)) return;
  const el = document.createElement("div");
  el.id = COVER_ID;
  el.style.cssText =
    "position:fixed;inset:0;z-index:2147483647;background:#0F1117;display:flex;flex-direction:column;" +
    "align-items:center;justify-content:center;gap:14px;color:rgba(255,255,255,.75);font:14px/1.4 inherit";
  el.innerHTML =
    '<div style="width:30px;height:30px;border:3px solid rgba(75,165,235,.25);border-top-color:#4BA5EB;' +
    'border-radius:50%;animation:ocesha-logout-spin 1s linear infinite"></div><div>Signing out…</div>' +
    "<style>@keyframes ocesha-logout-spin{to{transform:rotate(360deg)}}</style>";
  (document.body || document.documentElement).appendChild(el);
};

const markLoggingOut = () => {
  (window as any)[FLAG] = true;
  showCover();
};

/** Call after clearing the session, instead of `window.location.href = REACT_APP_LOGOUT_DOMAIN`. */
export const redirectAfterLogout = (url: string = logoutDomain()) => {
  markLoggingOut();
  // replace: "Back" from the logout site must not return to a logged-out app page
  window.location.replace(url || "/");
};

// Safety net for any logout path still doing `window.location.href = ...` directly: when the page
// starts leaving while logged out, hide it the same way. Undone if the page comes back from cache.
let installed = false;
export const installLogoutGuard = () => {
  if (installed) return;
  installed = true;
  window.addEventListener("beforeunload", () => {
    if (localStorage.getItem("logged_in") !== "true") markLoggingOut();
  });
  window.addEventListener("pageshow", (e: PageTransitionEvent) => {
    if (!e.persisted) return;
    (window as any)[FLAG] = false;
    document.getElementById(COVER_ID)?.remove();
  });
};
